import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Home, LifeBuoy, RotateCcw } from "lucide-react";
import { OrzioLogo } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { featureTranslations } from "@/i18n/featureTranslations";
import { translations, type Language } from "@/i18n/translations";
import { captureAppException } from "@/lib/monitoring";

type Props = {
  children: ReactNode;
  /** Email shown to users on the support link. Defaults to a build-time placeholder. */
  supportEmail?: string;
};

type State = {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
};

const isDevBuild = import.meta.env.DEV;
const DEFAULT_SUPPORT_EMAIL = "suporte@orzio.app";

const normalizeBoundaryLanguage = (value: string | null | undefined): Language => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "pt";
  if (normalized.startsWith("fr")) return "en";
  if (normalized.startsWith("en")) return "en";
  if (normalized.startsWith("es")) return "es";
  if (normalized.startsWith("pt")) return "pt";
  return "pt";
};

const detectBoundaryLanguage = (): Language => {
  if (typeof window === "undefined") return "pt";

  try {
    return normalizeBoundaryLanguage(window.localStorage.getItem("orca-language"));
  } catch {
    return "pt";
  }
};

const formatTemplate = (
  template: string,
  replacements: Record<string, string | number> = {},
): string =>
  Object.entries(replacements).reduce(
    (acc, [key, value]) => acc.replaceAll(`{${key}}`, String(value)),
    template,
  );

const translateBoundary = (
  language: Language,
  key: string,
  replacements?: Record<string, string | number>,
): string => {
  const template =
    featureTranslations[language]?.[key] ??
    translations[language]?.[key] ??
    featureTranslations.pt[key] ??
    translations.pt[key] ??
    key;

  return formatTemplate(template, replacements);
};

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, eventId: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, eventId: null };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    captureAppException(
      error,
      {
        feature: "app_error_boundary",
        route: typeof window !== "undefined" ? window.location.pathname : "unknown",
        code: error.name,
      },
      (eventId) => this.setState({ eventId }),
    );
  }

  private reset = () => {
    this.setState({ hasError: false, error: null, eventId: null });
  };

  private reload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  private goHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, eventId } = this.state;
    const language = detectBoundaryLanguage();
    const supportEmail = this.props.supportEmail ?? DEFAULT_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      translateBoundary(language, "errorBoundary.mail.subject", {
        eventIdSuffix: eventId ? ` (${eventId})` : "",
      }),
    );
    const body = encodeURIComponent(
      [
        translateBoundary(language, "errorBoundary.mail.greeting"),
        "",
        translateBoundary(language, "errorBoundary.mail.intro"),
        eventId
          ? translateBoundary(language, "errorBoundary.mail.reference", { eventId })
          : null,
        typeof window !== "undefined"
          ? translateBoundary(language, "errorBoundary.mail.url", {
              url: window.location.href,
            })
          : null,
        "",
        translateBoundary(language, "errorBoundary.mail.userAction"),
        "",
      ]
        .filter(Boolean)
        .join("\n"),
    );

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="orzio-light min-h-screen app-bg flex items-center justify-center p-6"
      >
        <div className="cockpit-card w-full max-w-lg rounded-2xl border-0 bg-white p-8 shadow-xl">
          <OrzioLogo size="sm" className="mb-6" />

          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          </div>

          <h1 className="text-xl font-semibold text-slate-900">
            {translateBoundary(language, "errorBoundary.title")}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {translateBoundary(language, "errorBoundary.description")}
          </p>

          {eventId && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <span className="text-slate-500">
                {translateBoundary(language, "errorBoundary.reference")}{" "}
              </span>
              <code className="font-mono text-slate-900">{eventId}</code>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Button onClick={this.reset} className="flex-1">
              <RotateCcw className="mr-2 h-4 w-4" />{" "}
              {translateBoundary(language, "errorBoundary.retry")}
            </Button>
            <Button variant="outline" onClick={this.reload} className="flex-1">
              {translateBoundary(language, "errorBoundary.reload")}
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
            <button
              type="button"
              onClick={this.goHome}
              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
            >
              <Home className="h-3.5 w-3.5" />{" "}
              {translateBoundary(language, "errorBoundary.goHome")}
            </button>
            <a
              href={`mailto:${supportEmail}?subject=${subject}&body=${body}`}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <LifeBuoy className="h-3.5 w-3.5" />{" "}
              {translateBoundary(language, "errorBoundary.contactSupport")}
            </a>
          </div>

          {isDevBuild && error && (
            <details className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
              <summary className="cursor-pointer font-medium text-slate-700">
                {translateBoundary(language, "errorBoundary.devDetails")}
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed text-slate-800">
                {error.message}
                {error.stack ? `\n\n${error.stack}` : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
